import {useRef, useCallback, useEffect, useState} from 'react'
import {ajaxApi, ajaxApiToken} from '@/server'
import debounce from 'lodash/debounce'

/**
 * @typedef SimpleRequestOptions
 * @property {any} [initialData] 初始数据(不会调用formatResult格式化)
 * @property {function} [formatResult] 结果格式化方法
 * @property {boolean} [manual] 是否手动触发，默认false，及自动调用run
 * @property {any[]} [defaultParams] 自动执行 run 的时候，默认带上的参数
 * @property {number} [debounce] 大于0时启用请求节流
 * @property {number} [loadingDelay] 设置显示 loading 的延迟时间，避免闪烁(暂未实现)
 */

/**
 * @typedef SimpleRequestRes
 * @property {unknown} data 数据
 * @property {loading} loading 正在请求service
 * @property {any[]} params 上次调用service所使用的参数（！！注意它是一个数组！！）
 * @property {function} refresh 使用上次参数再次调用service
 * @property {any[]} run 使用新的参数执行service
 * @property {undefined|Error|any} error 发生错位时的错误信息，没有错误时为undefined
 * @property {(newData: any) => void | ((oldData: any) => any) => void} mutate 手动修改data数据，可以直接传入新值，如果参数是函数，则以当前data为参数传入此函数，并返回新数据
 */

/**
 * useCallbackRef - 类似于useCallback
 * 能在返回的引用不改变的情况下获取到组件最新状态，适用于作为回调传递给使用React.memo包裹的子组件以提高性能
 * @param {Function} callback
 * @return {function(...[*]): *}
 */
export function useCallbackRef (callback) {
  const ref = useRef(callback)
  ref.current = callback

  return useCallback((...args) => {
    return ref.current(...args)
  }, [])
}

/**
 * useAsyncEffect - useEffect 的允许传入异步函数的版本
 * @param {AsyncGeneratorFunction} fn
 * @param {any[]} [deps]
 */
export function useAsyncEffect (fn, deps) {
  useEffect(() => {
    fn()
  }, deps)
}

/**
 * useSimpleRequest 封装请求的简易hook，封装了 结果、loading状态、错误信息及支持请求节流。
 * (run函数在非debounce模式时，返回Promise，debounce模式无返回结果)
 * 更复杂的可使用 ahooks/useRequest
 *
 * @param {GeneratorFunction|Function} service
 * @param {SimpleRequestOptions} [opts]
 * @return {SimpleRequestRes}
 */
export function useSimpleRequest (service, opts = {}) {
  const [data, setData] = useState(opts.initialData)
  const [loading, setLoading] = useState(!opts.manual) // 自动模式下，默认时加载状态
  const [error, setError] = useState(undefined)
  const paramsRef = useRef(opts.defaultParams || [])
  const debouncedRunRef = useRef()

  const request = async () => {
    try {
      setLoading(true)
      let res = await service(...paramsRef.current)
      if (opts.formatResult) {
        res = opts.formatResult(res, paramsRef.current)
      }
      setData(res)
      if (opts.onSuccess) {
        opts.onSuccess(res, paramsRef.current)
      }
      return res
    } catch (e) {
      setError(e)
      throw e
    } finally {
      setLoading(false)
    }
  }

  const run = (...args) => {
    paramsRef.current = [...args]
    if (debouncedRunRef.current) {
      debouncedRunRef.current()
    } else {
      return request()
    }
  }

  const mutate = (newData) => {
    let res
    if (typeof newData === 'function') {
      res = newData(data)
    } else {
      res = newData
    }
    setData(res)
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && opts.defaultParams && !Array.isArray(opts.defaultParams)) {
      console.error('Param "defaultParams" must be an array type')
    }

    if (!opts.manual) {
      request()
    }
  }, [])

  useEffect(() => {
    if (opts.debounce && opts.debounce > 0) {
      debouncedRunRef.current = debounce(request, opts.debounce)
    } else {
      debouncedRunRef.current = undefined
    }
  }, [opts.debounce])

  return {
    params: paramsRef.current,
    run,
    mutate,
    refresh: request,
    data,
    error,
    loading
  }
}

/**
 * ajaxApi 的hooks形式
 * @param url
 * @param {SimpleRequestOptions} opts
 * @return {{data: unknown, refresh: () => void, run: (...args: any[]) => void, params: any[], error: any, loading: boolean}}
 */
export function useAjaxApi (url, opts) {
  const formatResult = (res, params) => opts.formatResult ? opts.formatResult(res.data, params) : res.data
  return useSimpleRequest((...params) => ajaxApi(url, ...params), {
    ...opts,
    formatResult
  })
}

/**
 * ajaxApi 的hooks形式，就是加了个必传的token参数
 * @param {string} url
 * @param {string} token
 * @param {SimpleRequestOptions} opts
 * @return {{data: unknown, refresh: () => void, run: (...args: any[]) => void, params: any[], error: any, loading: boolean}}
 */
export function useAjaxApiWithToken (url, token, opts) {
  const formatResult = (res, params) => opts.formatResult ? opts.formatResult(res.data, params) : res.data
  return useSimpleRequest((params, errorCallback) => ajaxApi(url, params, errorCallback, token), {
    ...opts,
    formatResult
  })
}

/**
 * ajaxApiToken 的hook形式
 * @param url
 * @param token
 * @param opts
 * @return {{data: unknown, refresh: () => void, run: (...args: any[]) => void, params: any[], error: unknown, loading: boolean}}
 */
export function useAjaxApiToken (url, token, opts) {
  const formatResult = (res, params) => opts.formatResult ? opts.formatResult(res.data, params) : res.data
  return useSimpleRequest((...params) => ajaxApiToken(url, token, ...params), {
    ...opts,
    formatResult
  })
}

export function useFirstMount () {
  const isFirst = useRef(true)

  if (isFirst.current) {
    isFirst.current = false

    return true
  }
  return isFirst.current
}

export const useUpdateEffect = (callback, deps) => {
  const isFirstMount = useFirstMount()
  useEffect(() => {
    if (!isFirstMount) {
      return callback()
    }
  }, deps)
}



// WEBPACK FOOTER //
// ./src/utils/hooks.js